import React, { useMemo, useState } from 'react'
import { getSevColor } from '@/constants'
import type { SliceMetrics } from '@/analysis/searchContract'
import { explainSeverity } from '@/utils/severityExplain'
import { asSeverity, fmtKbps, fmtLagMs, fmtRatio, getDemuxInRateText } from './format'
import SeverityHex from './SeverityHex'
import { humanizeMetricKey } from './detailRows'

/**
 * Generic chart-mirror table: a tabular view of EXACTLY the entities the active
 * chart renders (hex tiles / world hops). Callers map their chart items to
 * `DetailRow`s from the same selector the chart uses, so table == chart by
 * construction. Row click re-uses the same drill gesture as clicking the
 * chart element.
 */
export type DetailRow = {
  id: string
  label: string
  /** Secondary label (e.g. hop type or location). */
  sub?: string | null
  severity: 0 | 1 | 2 | null
  /** Worst-metric severity when known; falls back to avg in the hex. */
  worstMetricSeverity?: 0 | 1 | 2 | null
  totalStreams: number | null
  usersTotal: number | null
  tickCount?: number | null
  worstMetric?: string | null
  metrics?: SliceMetrics | null
  onActivate?: () => void
}

type SortKey = 'severity' | 'label' | 'streams' | 'users'

const METRIC_COLS: Array<{ key: keyof SliceMetrics; label: string; fmt: (v: number | null) => string }> = [
  { key: 'demuxToDecodeLag', label: 'Demux→Dec', fmt: (v) => fmtLagMs(v) },
  { key: 'renderRatio', label: 'Render i/o', fmt: (v) => fmtRatio(v) },
  { key: 'downloadKbps', label: 'Download', fmt: (v) => fmtKbps(v) },
  { key: 'mediaLatency', label: 'E2E latency', fmt: (v) => fmtLagMs(v) },
]

function metricCell(metrics: SliceMetrics | null | undefined, col: (typeof METRIC_COLS)[number]) {
  const cell = metrics?.[col.key]
  const v = typeof cell?.displayValue === 'number' && Number.isFinite(cell.displayValue) ? cell.displayValue : null
  const tier = asSeverity(cell?.tier)
  return (
    <span style={{ color: tier == null ? 'var(--t-1)' : getSevColor(tier), fontWeight: 600 }}>
      {v == null ? '—' : col.fmt(v)}
    </span>
  )
}

function demuxRateCell(metrics: SliceMetrics | null | undefined) {
  const tier = asSeverity(metrics?.demuxInRate?.tier)
  return (
    <span style={{ color: tier == null ? 'var(--t-1)' : getSevColor(tier), fontWeight: 600 }}>
      {getDemuxInRateText(tier)}
    </span>
  )
}

function sevRank(s: 0 | 1 | 2 | null): number {
  return s == null ? 3 : s
}

const th: React.CSSProperties = {
  position: 'sticky', top: 0, zIndex: 2,
  background: 'var(--bg-2)', color: 'var(--t-2)',
  fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em',
  textAlign: 'left', padding: '6px 10px', borderBottom: '0.5px solid var(--bd-1)',
  cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
}
const td: React.CSSProperties = {
  padding: '6px 10px', fontSize: 11, color: 'var(--t-0)',
  borderBottom: '0.5px solid var(--bd-0)', whiteSpace: 'nowrap',
}

export default function DetailTable({
  rows,
  emptyText,
  showStreams = true,
}: {
  rows: DetailRow[]
  emptyText: string
  showStreams?: boolean
}) {
  const [sortKey, setSortKey] = useState<SortKey>('severity')
  const [asc, setAsc] = useState(true)

  const sorted = useMemo(() => {
    const out = [...rows]
    out.sort((a, b) => {
      let d = 0
      if (sortKey === 'severity') d = sevRank(a.severity) - sevRank(b.severity)
      else if (sortKey === 'label') d = a.label.localeCompare(b.label)
      else if (sortKey === 'streams') d = (a.totalStreams ?? 0) - (b.totalStreams ?? 0)
      else d = (a.usersTotal ?? 0) - (b.usersTotal ?? 0)
      if (d === 0) d = a.label.localeCompare(b.label)
      return asc ? d : -d
    })
    return out
  }, [rows, sortKey, asc])

  const header = (key: SortKey, label: string) => (
    <th
      style={th}
      onClick={() => {
        if (sortKey === key) setAsc((v) => !v)
        else { setSortKey(key); setAsc(true) }
      }}
    >
      {label}{sortKey === key ? (asc ? ' ▲' : ' ▼') : ''}
    </th>
  )

  if (!rows.length) {
    return (
      <div className="panel" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--t-2)', fontSize: 12 }}>
        {emptyText}
      </div>
    )
  }

  return (
    <div className="panel" style={{ height: '100%', overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {header('severity', 'Sev')}
            {header('label', 'Name')}
            {showStreams && header('streams', 'Streams')}
            {header('users', 'Users')}
            <th style={{ ...th, cursor: 'default' }}>Ticks</th>
            <th style={{ ...th, cursor: 'default' }}>Worst metric</th>
            <th style={{ ...th, cursor: 'default' }}>Demux rate</th>
            {METRIC_COLS.map((c) => (
              <th key={c.key} style={{ ...th, cursor: 'default' }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr
              key={r.id}
              onClick={r.onActivate}
              style={{ cursor: r.onActivate ? 'pointer' : 'default' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-3)' }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent' }}
            >
              <td style={td}>
                <SeverityHex
                  avg={r.severity}
                  worst={r.worstMetricSeverity}
                  tooltip={explainSeverity({
                    avgSeverity: r.severity,
                    worstMetric: r.worstMetric,
                    worstMetricSeverity: r.worstMetricSeverity,
                    metrics: r.metrics,
                  })}
                />
              </td>
              <td style={{ ...td, fontWeight: 600 }}>
                <div>{r.label}</div>
                {r.sub ? (
                  <div style={{ color: 'var(--t-2)', fontWeight: 400, fontSize: 10, marginTop: 1 }}>
                    {r.sub}
                  </div>
                ) : null}
              </td>
              {showStreams && <td style={td}>{r.totalStreams ?? '—'}</td>}
              <td style={td}>{r.usersTotal ?? '—'}</td>
              <td style={td}>{r.tickCount ?? '—'}</td>
              <td style={{ ...td, color: 'var(--t-1)' }}>{humanizeMetricKey(r.worstMetric) ?? '—'}</td>
              <td style={td}>{demuxRateCell(r.metrics)}</td>
              {METRIC_COLS.map((c) => (
                <td key={c.key} style={td}>{metricCell(r.metrics, c)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
